import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Printer, Download, ImagePlus, Loader2 } from "lucide-react";
import { endpoints, apiError, API_BASE } from "@/lib/api";

const SITE = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
const abs = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

const UploadBtn = ({ kind, label, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (f) => {
    if (!f) return;
    setBusy(true);
    try { const { data } = await endpoints.merchantBranding(kind, f); onDone(kind, data.url); toast.success(`${label} updated`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <button type="button" onClick={() => ref.current.click()} disabled={busy} className="h-10 px-4 rounded-full border border-[#273042] text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`merchant-upload-${kind}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {label}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => up(e.target.files?.[0])} data-testid={`merchant-upload-${kind}-input`} />
    </button>
  );
};

// Printable poster: shop photo + logo, QR (opens /pay with the code filled) and the merchantscode.
export const MerchantPoster = ({ m: initial }) => {
  const [m, setM] = useState(initial);
  const url = `${SITE}/pay?code=${m.merchantscode}`;
  const done = (kind, u) => setM((x) => ({ ...x, [`${kind}Url`]: u }));
  const download = () => {
    const svg = document.getElementById("merchant-qr-svg");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" }));
    a.download = `bbkigali-pay-${m.merchantscode}.svg`;
    a.click();
  };
  const print = () => { document.body.classList.add("printing-poster"); window.print(); setTimeout(() => document.body.classList.remove("printing-poster"), 500); };
  return (
    <div className="rounded-2xl bg-[#121620] border border-[#273042] p-5" data-testid="merchant-poster-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 print:hidden">
        <div className="text-white font-bold">Your QR poster</div>
        <div className="flex flex-wrap gap-2">
          <UploadBtn kind="logo" label={m.logoUrl ? "Change logo" : "Add logo"} onDone={done} />
          <UploadBtn kind="photo" label={m.photoUrl ? "Change shop photo" : "Add shop photo"} onDone={done} />
          <button onClick={download} className="h-10 px-4 rounded-full border border-[#273042] text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid="merchant-qr-download"><Download className="w-4 h-4" /> QR image</button>
          <button onClick={print} className="h-10 px-4 rounded-full bg-[#E11D2A] text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid="merchant-poster-print"><Printer className="w-4 h-4" /> Print poster</button>
        </div>
      </div>
      <div id="merchant-poster" className="mx-auto max-w-sm bg-white text-black rounded-2xl overflow-hidden text-center" data-testid="merchant-poster">
        {m.photoUrl && <img src={abs(m.photoUrl)} alt="" className="w-full h-36 object-cover" data-testid="merchant-poster-photo" />}
        <div className="px-8 pb-8 pt-6">
          {m.logoUrl && <img src={abs(m.logoUrl)} alt="" className={`w-20 h-20 rounded-full object-cover border-4 border-white shadow mx-auto ${m.photoUrl ? "-mt-16" : ""}`} data-testid="merchant-poster-logo" />}
          <div className="text-xs font-extrabold tracking-[0.3em] text-[#E11D2A] mt-2">B&B KIGALI PAY</div>
          <div className="text-2xl font-extrabold mt-2 leading-tight">{m.businessName}</div>
          <div className="text-xs text-gray-500 mt-1">{m.location?.sector}, {m.location?.district}</div>
          <div className="my-5 inline-block p-3 rounded-xl border-4 border-[#E11D2A]"><QRCodeSVG id="merchant-qr-svg" value={url} size={190} level="M" data-testid="merchant-qr" /></div>
          <div className="text-sm font-bold">Scan to pay with MTN MoMo</div>
          <div className="text-xs text-gray-500 mt-3">or open the B&B KIGALI app → Pay → enter</div>
          <div className="text-5xl font-extrabold tracking-[0.25em] mt-1" data-testid="merchant-poster-code">{m.merchantscode}</div>
        </div>
      </div>
    </div>
  );
};

// WhatsApp payment link with the amount already filled in.
export const PaymentLink = ({ m }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const amt = Number(amount) || 0;
  const link = `${SITE}/pay?code=${m.merchantscode}${amt ? `&amount=${amt}` : ""}${note ? `&note=${encodeURIComponent(note.slice(0, 60))}` : ""}`;
  const text = `Pay ${amt ? `${amt.toLocaleString()} RWF ` : ""}to ${m.businessName}${note ? ` for ${note}` : ""} with B&B KIGALI Pay (MTN MoMo): ${link}`;
  const copy = () => navigator.clipboard?.writeText(link).then(() => toast.success("Link copied"));
  return (
    <div className="rounded-2xl bg-[#121620] border border-[#273042] p-5" data-testid="merchant-paylink">
      <div className="text-white font-bold mb-1">Share a payment link</div>
      <p className="text-xs text-slate-400 mb-3">Send a link on WhatsApp — the customer opens it and only has to approve the MoMo prompt.</p>
      <div className="flex flex-wrap gap-2">
        <input type="number" min={100} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (RWF)" className="h-11 w-40 px-3 rounded-xl bg-[#0A0C10] border border-[#273042] text-white" data-testid="paylink-amount" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What for? (optional)" maxLength={60} className="h-11 flex-1 min-w-[160px] px-3 rounded-xl bg-[#0A0C10] border border-[#273042] text-white" data-testid="paylink-note" />
      </div>
      <div className="mt-2 text-[11px] text-slate-500 break-all font-mono" data-testid="paylink-url">{link}</div>
      <div className="flex gap-2 mt-3">
        <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" className={`h-11 px-5 rounded-full bg-[#25D366] text-black font-bold text-sm inline-flex items-center ${amt && amt < 100 ? "pointer-events-none opacity-50" : ""}`} data-testid="paylink-whatsapp">Share on WhatsApp</a>
        <button onClick={copy} className="h-11 px-5 rounded-full border border-[#273042] text-white font-bold text-sm" data-testid="paylink-copy">Copy link</button>
      </div>
    </div>
  );
};
